// client/src/components/chat/ChatMessageItem.js
import React from 'react';
import { Box, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import ReputationBadge from '../reputation/ReputationBadge';
import ItemShareCard from './ItemShareCard';

function formatTime(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
}

// Indigo-palette trust colours matching the app theme
const TRUST_COLORS = {
  0: 'rgba(230,234,242,0.5)',
  1: '#E6EAF2',
  2: '#7C8CFF',
  3: '#4CAF50',
  4: '#FF9800',
  5: '#FF7AC3',
};

function Username({ user }) {
  const color = TRUST_COLORS[user?.trustLevel || 0];
  const name = user?.username || 'Unknown';
  const id = user?._id;

  const sx = {
    color,
    fontWeight: 700,
    flexShrink: 0,
    textDecoration: 'none',
    '&:hover': { textDecoration: 'underline' },
  };

  if (id) {
    return (
      <Typography variant="body2" component={Link} to={`/users/${id}`} sx={sx}>
        {name}
      </Typography>
    );
  }
  return <Typography variant="body2" component="span" sx={{ ...sx, cursor: 'default' }}>{name}</Typography>;
}

export default function ChatMessageItem({ message, isOwn }) {
  const user = message.userId;
  const time = formatTime(message.createdAt);

  if (message.type === 'system') {
    return (
      <Box sx={{ py: 0.25, px: 1.5 }}>
        <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
          {message.content}
        </Typography>
      </Box>
    );
  }

  if (message.type === 'action') {
    return (
      <Box sx={{ py: 0.25, px: 1.5, display: 'flex', alignItems: 'baseline', gap: 1 }}>
        <Typography variant="caption" sx={{ color: 'text.disabled', flexShrink: 0 }}>{time}</Typography>
        <Typography variant="body2" sx={{ color: 'secondary.main', fontStyle: 'italic' }}>
          * {user?.username} {message.content}
        </Typography>
      </Box>
    );
  }

  if (message.type === 'item-share') {
    return (
      <Box sx={{ py: 0.75, px: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5, flexWrap: 'wrap' }}>
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>{time}</Typography>
          <Username user={user} />
          <ReputationBadge trustLevel={user?.trustLevel} />
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>shared an item</Typography>
        </Box>
        <ItemShareCard item={message.sharedItem} />
      </Box>
    );
  }

  // Standard message
  return (
    <Box sx={{
      py: 0.375, px: 1.5,
      display: 'flex', alignItems: 'baseline', gap: 1,
      bgcolor: isOwn ? 'rgba(124,140,255,0.05)' : 'transparent',
      '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' },
    }}>
      <Typography variant="caption" sx={{ color: 'text.disabled', flexShrink: 0 }}>{time}</Typography>
      <Username user={user} />
      <ReputationBadge trustLevel={user?.trustLevel} />
      <Typography variant="body2" sx={{ color: 'text.primary', wordBreak: 'break-word' }}>
        {message.content}
      </Typography>
    </Box>
  );
}
